/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import {
  AcademicHistory,
  ListGrades,
} from "../../../components/Student/MyGrades";
import { Loader, Select, Input, Button, Icon } from "semantic-ui-react";
import { Student } from "../../../api";
import { useAuth } from "../../../hooks";
import { BasicModal } from "../../../components/Shared";
import "./MyGrades.scss";

const studentController = new Student();

export function MyGrades(props) {
  const { onReload } = props;
  const { user, accessToken } = useAuth();

  const [grades, setGrades] = useState(null);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [studentActive, setStudentActive] = useState(null);
  const [academicCycles, setAcademicCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [academicHistoryActive, setAcademicHistoryActive] = useState(null);
  const [showModalAcademicHistory, setShowModalAcademicHistory] =
    useState(false);

  const onOpenCloseModalAcademicHistory = (row) => {
    if (row && row.SyllabuSubjectGrade && row.SyllabuSubjectGrade.subject) {
      setAcademicHistoryActive(row);
      setShowModalAcademicHistory(true);
      setSelectedCourse(row.SyllabuSubjectGrade.subject.name);
    } else {
      setShowModalAcademicHistory(false);
      setAcademicHistoryActive(null);
      setSelectedCourse(null);
    }
  };

  const getAverageGradeColor = (averageGrade) => {
    if (averageGrade < 12) return "red";
    return "green";
  };

  const getAverageGradeText = (averageGrade) => {
    return `${averageGrade}`;
  };

  const getProgressColor = (progress) => {
    if (progress < 60) return "red";
    if (progress < 80) return "yellow";
    return "green";
  };

  const getProgressText = (progress) => {
    return `${progress}%`;
  };

  const getStatusColor = (fieldValue, row) => {
    // Si recoveryStatus = 2, significa que hay nota de recuperación
    if (row.recoveryStatus === 2) {
      if (row.recoveryGrade >= 11) return "blue"; // Recuperado - azul
      return "red"; // Desaprobado
    }
    // Si no tiene recuperación, evaluar por promedio normal
    if (row.averageGrade >= 11) return "green";
    if (row.averageGrade <= 10) return "red";
    return "brown";
  };

  const getStatusText = (fieldValue, row) => {
    // Si recoveryStatus = 2, significa que hay nota de recuperación
    if (row.recoveryStatus === 2) {
      if (row.recoveryGrade >= 11) return "RECUPERADO";
      return "DESAPROBADO";
    }
    // Si no tiene recuperación, evaluar por promedio normal
    if (row.averageGrade >= 11) return "APROBADO";
    if (row.averageGrade <= 10) return "DESAPROBADO";
    return "SIN DEFINIR";
  };

  const getRecoveryGradeDisplay = (row) => {
    // Si recoveryStatus = 1, mostrar "-"
    if (row.recoveryStatus === 1) return "-";
    // Si recoveryStatus = 2, mostrar la nota
    if (row.recoveryStatus === 2) return row.recoveryGrade || "-";
    // En cualquier otro caso, mostrar "-"
    return "-";
  };

  const tableConfig = {
    headers: [
      "Asignatura",
      "Progreso",
      "Cuestionarios",
      "Trabajo Final",
      "Conducta",
      "Recuperación",
      "Promedio",
      "Estado",
      "Acciones",
    ],
    fields: [
      {
        field: "SyllabuSubjectGrade.subject.name",
        dataType: "text",
      },
      {
        field: `progress+ +"%"`,
        component: "Label",
        getColor: getProgressColor,
        getText: getProgressText,
        align: "center",
      },
      {
        field: "questionnaireGrade",
        dataType: "text",
        align: "center",
      },
      {
        field: "homeworkGrade",
        dataType: "text",
        align: "center",
      },
      {
        field: "conductGrade",
        dataType: "text",
        align: "center",
      },
      {
        field: "recoveryGrade",
        component: "custom",
        align: "center",
        render: (row) => getRecoveryGradeDisplay(row),
      },
      {
        field: "averageGrade",
        dataType: "text",
        align: "center",
      },
      {
        field: "recoveryStatus",
        component: "Label",
        getColor: getStatusColor,
        getText: getStatusText,
        align: "center",
      },
      {
        buttons: [
          {
            color: "orange",
            icon: "line chart",
            placeholder: "Historial Académico",
            onClick: onOpenCloseModalAcademicHistory,
          },
        ],
      },
    ],
    columnWidths: ["20%", "10%", "10%", "10%", "10%", "10%", "10%", "10%"],
  };

  // Cargar los ciclos académicos del estudiante
  useEffect(() => {
    (async () => {
      try {
        const cycles = await studentController.getAcademicCyclesByUser(
          user.id,
          accessToken
        );
        setAcademicCycles(cycles);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [user.id, accessToken]);

  useEffect(() => {
    // Cargar los pagos del estudiante para el ciclo académico seleccionado
    if (selectedCycle > 0) {
      (async () => {
        setLoadingGrades(true);
        try {
          const response = await studentController.getAcademicAdvanceByStudent(
            accessToken,
            user.id,
            selectedCycle
          );
          setGrades(response.courses);
          setStudentActive(response.studentId);
        } catch (error) {
          console.error(error);
        } finally {
          setLoadingGrades(false);
        }
      })();
    } else {
      setGrades([]);
      setLoadingGrades(false);
    }
  }, [accessToken, user.id, selectedCycle]);

  const handleCycleChange = (e, { value }) => {
    setSelectedCycle(value);
    setStudentActive(null); // Restablece el selector de grado
    setGrades([]); // Limpia las calificaciones
  };

  if (!grades)
    return (
      <div className="grades__list">
        <Loader active inline="centered" />
      </div>
    );

  return (
    <div className="student-my-grades">
      <h1 className="student-my-grades__title">Mis Calificaciones</h1>
      <p className="student-my-grades__description">
        Aquí puedes ver todos tus calificaciones de tu año académico y grado
        seleccionado, además de tu historial académico.
      </p>
      <div className="student-my-grades__filters">
        <div className="student-my-grades__filters-left">
          <Select
            className="edunova-select"
            placeholder="Selecciona un Año académico"
            options={[
              { key: "", value: "", text: "Selecciona un ciclo académico" },
              ...academicCycles.map((cycle) => ({
                key: cycle.id,
                value: cycle.id,
                text: (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: `Año académico: <b>${cycle.Level.name} - ${cycle.name}</b>`,
                    }}
                  />
                ),
              })),
            ]}
            value={selectedCycle}
            onChange={handleCycleChange}
            clearable
          />
        </div>
        <div className="student-my-grades__filters-right">
          <Button
            onClick={() => {
              setSelectedCycle(null);
              setGrades([]);
            }}
          >
            <Icon name="refresh" />
            Limpiar filtros
          </Button>
        </div>
      </div>
      <ListGrades
        grades={grades}
        loading={loadingGrades || !grades}
        error={null}
        tableConfig={tableConfig}
        onReload={onReload}
      />
      <BasicModal
        size="large"
        title={`Historial Académico - ${selectedCourse}`}
        show={showModalAcademicHistory}
        close={onOpenCloseModalAcademicHistory}
        className="academic-history-modal"
      >
        <AcademicHistory
          academicHistory={academicHistoryActive}
          studentId={studentActive}
        />
      </BasicModal>
    </div>
  );
}
